// Shows what a conveyance claim will actually reimburse, while it is being
// filed.
//
// The differential is applied at submit time, so without this the employee
// discovers the smaller figure only after the fact — which reads as the company
// quietly shaving their claim. Showing the arithmetic up front makes it a rule
// rather than a surprise.
//
// Kept out of Expenses.tsx (already ~2k lines) and given its own polling hook so
// the form only has to render one element.

import { useEffect, useState } from 'react';
import { Info, AlertTriangle, Route } from 'lucide-react';
import { previewConveyance, type ConveyancePreview } from '../api/policyMoney';

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

interface Props {
  spendDate: string | null;
  lines: Array<{ category: string; amount: number }>;
}

export default function ConveyanceNotice({ spendDate, lines }: Props) {
  const [preview, setPreview] = useState<ConveyancePreview | null>(null);

  // Serialised so the effect only refires on a real change, not on every
  // keystroke that rebuilds the array identity.
  const key = JSON.stringify({ spendDate, lines });

  useEffect(() => {
    let cancelled = false;
    const priced = lines.filter((l) => l.category && l.amount > 0);
    if (!spendDate || priced.length === 0) {
      setPreview(null);
      return;
    }
    previewConveyance({ spend_date: spendDate, lines: priced })
      .then((res) => { if (!cancelled) setPreview(res); })
      // Advisory only — a failed preview must never block filing a claim.
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!preview || !preview.differential_applies) return null;

  const { adjustments, total_claimed, total_reimbursable, checks } = preview;
  const issues = checks?.issues ?? [];

  if (preview.missing_declaration) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <p>
          Conveyance here is reimbursed as the amount <em>above</em> your usual
          commute, but you haven't declared that yet. Add it on the Allowances
          page — until then this claim goes through at its full amount.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {adjustments.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <div className="flex items-start gap-2">
            <Route size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">Conveyance is reimbursed above your usual commute</p>
              <p className="mt-0.5 text-xs">
                You'd have spent {money(preview.declared_commute || 0)} getting to
                work anyway, so that part isn't reimbursed.
              </p>
              <table className="mt-2 w-full text-xs">
                <tbody>
                  {adjustments.map((a) => (
                    <tr key={a.line_index}>
                      <td className="py-0.5 pr-2">{a.category}</td>
                      <td className="py-0.5 pr-2 text-right">{money(a.claimed_amount)}</td>
                      <td className="py-0.5 pr-2 text-right text-blue-700">
                        − {money(a.offset)}
                      </td>
                      <td className="py-0.5 text-right font-semibold">
                        {money(a.reimbursable_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-blue-200">
                    <td className="pt-1 font-medium">Reimbursable</td>
                    <td className="pt-1 pr-2 text-right">{money(total_claimed)}</td>
                    <td />
                    <td className="pt-1 text-right font-bold">{money(total_reimbursable)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {issues.map((issue) => (
        <div
          key={issue.code}
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <Info size={16} className="mt-0.5 shrink-0" />
          {/* Advisory: the approver still sees and decides the claim. */}
          <p>{issue.message}</p>
        </div>
      ))}
    </div>
  );
}
